import { computed, inject, Injectable, signal } from '@angular/core';
import { RegisterUserFormValues } from '../interfaces';
import { VisitorRegistrationBeforeModularFormField } from '../interfaces/register-user.response';
import { TYPE_OF_REGISTER_MODULAR_FORM_FIELD } from '../components/register-user-form/config';
import { toCountryAnswer } from '../utils/country-answer';
import { RegisterStandaloneService } from '../../register-standalone/register-standalone.service';
import { TranslationService } from 'src/app/services';
import { TripType } from '../interfaces/register-user.response';

/**
 * Pantalla activa del registro simple. `register-after` (HRS-3768) solo aparece si
 * el registro configurado tiene preguntas para después.
 */
export type SimpleRegisterStep = 'form' | 'register-after' | 'user-data';

/**
 * HRS-2990: estado del registro simple del informador (rutas
 * `tech-register-by-token/:token` y `tech-register-by-id/:id`).
 *
 * El formulario pasa a ser el unificado del planner, pero el tramo final —el
 * registro posterior, nombre/email, PDFs, envío a `post-register` y encuesta— es
 * el que ya estaba en producción: al completar el formulario se vuelcan los
 * valores en `RegisterStandaloneService` y se cede el paso a sus componentes.
 */
@Injectable({ providedIn: 'root' })
export class SimpleRegisterFlow {
  private _registerStandaloneService = inject(RegisterStandaloneService);
  private _translationService = inject(TranslationService);

  /** Token del canal de registro, cuando se entra por QR / canal. */
  private _channelToken = signal<string | null>(null);
  public channelToken = this._channelToken.asReadonly();

  /** Id del registro configurado, cuando se entra por el acceso directo del panel. */
  private _registerId = signal<string | null>(null);
  public registerId = this._registerId.asReadonly();

  private _step = signal<SimpleRegisterStep>('form');
  public step = this._step.asReadonly();

  public isActive = computed(() => this._channelToken() !== null || this._registerId() !== null);

  public setChannelToken(token: string): void {
    this._channelToken.set(token);
    this._registerStandaloneService.tokenRegister = token;
  }

  public setRegisterId(id: string): void {
    this._registerId.set(id);
    this._registerStandaloneService.idRegister = id;
  }

  public reset(): void {
    this._channelToken.set(null);
    this._registerId.set(null);
    this._step.set('form');
  }

  public backToForm(): void {
    this._step.set('form');
  }

  /**
   * Cierra el formulario unificado: traduce sus valores al contrato que ya consume
   * `post-register` y abre el tramo final.
   */
  public completeForm(
    values: RegisterUserFormValues,
    modularFieldDefinitions: VisitorRegistrationBeforeModularFormField[] = [],
  ): void {
    const service = this._registerStandaloneService;

    service.setCalendarData({
      startDate: toApiDate(values.startDate),
      endDate: toApiDate(values.endDate ?? values.startDate),
    });

    // HRS-2990: la hora se quedaba en el formulario; hace falta para instanciar la
    // ruta predefinida del correo a la hora en que el visitante empieza.
    service.setStartTime(values.startTime ?? null);

    const tripTypeId = (values.tripType as unknown as TripType)?.id ?? null;
    if (tripTypeId !== null) service.setTravelerType(tripTypeId);

    // El back recorre `selectedCategories` leyendo `subCategories` (con C alta) y
    // hace count() sobre esa clave, así que siempre viaja, aunque sea vacía.
    service.setCategories(
      (values.categories ?? []).map(category => ({
        id: category.id,
        subCategories: category.subcategories ?? [],
      })),
    );

    service.setRegisterBeforeAnswer(toRegisterAnswers(values, modularFieldDefinitions));

    this._step.set(this._hasRegisterAfter() ? 'register-after' : 'user-data');
  }

  /**
   * HRS-3768: cierra el paso del registro posterior. Sus respuestas viajan en el
   * mismo formato que las del previo, que es el que ya guarda `post-register`.
   */
  public completeRegisterAfter(
    values: Record<string, unknown>,
    fieldDefinitions: VisitorRegistrationBeforeModularFormField[],
  ): void {
    this._registerStandaloneService.setRegisterAfterAnswer(
      toRegisterAnswers(values as RegisterUserFormValues, fieldDefinitions),
    );

    this._step.set('user-data');
  }

  private _hasRegisterAfter(): boolean {
    const questions = this._registerStandaloneService.registerAfterQuestion();
    return !!questions?.[this._translationService.selectedLanguage()]?.length;
  }
}

/** `DD/MM/YYYY` del formulario -> `YYYY-MM-DD`, que es lo que parsea el back. */
function toApiDate(value: string | null): string | null {
  if (!value) return null;
  const [day, month, year] = value.split('/');
  return year && month && day ? `${year}-${month}-${day}` : value;
}

/**
 * Campos modulares del formulario -> respuestas del registro, en el mismo formato
 * que ya guarda `post-register` y que lee el listado de Resultados de viajeros.
 */
function toRegisterAnswers(
  values: RegisterUserFormValues,
  definitions: VisitorRegistrationBeforeModularFormField[],
): { question: string; type: string; answer: unknown; required: boolean }[] {
  const answers: { question: string; type: string; answer: unknown; required: boolean }[] = [];

  definitions.forEach((field, index) => {
    const value = values[`${field.type}_${index}`];
    if (value == null) return;

    // El país viaja como iata (ES, PT…), que es lo que el CMS guarda y filtra.
    const answer: unknown = field.type === TYPE_OF_REGISTER_MODULAR_FORM_FIELD.country
      ? toCountryAnswer(value)
      : value;

    answers.push({
      // Las preguntas de texto traen su titular en `name`, no en `title`.
      question: field.title || field.name || field.type,
      type: field.type,
      answer,
      required: field.required,
    });
  });

  return answers;
}
